import { ResidentialComplexRead, ReportConfig, ReportConfigColumn } from '../api/types';

export type ReportColumnFormValue = {
  id: string;
  key: string;
  title: string;
};

export type ReportStatusLabelFormValue = {
  id: string;
  key: string;
  label: string;
};

export type ReportSettingsFormValue = {
  report_anchor_day: number | '';
  report_fallback_days: number | '';
  report_timezone: string;
  export_mode: 'combined' | 'separate_by_resource';
  export_format: 'xlsx' | 'c300_csv';
  columns: ReportColumnFormValue[];
  statusLabels: ReportStatusLabelFormValue[];
  deviceTypeColumns: Record<string, ReportColumnFormValue[]>;
};

export type ReportSettingsValidationErrors = {
  report_anchor_day: string;
  report_fallback_days: string;
  report_timezone: string;
  columns: string;
  statusLabels: string;
  deviceTypeColumns: Record<string, string>;
};

export const createEmptyReportSettingsErrors = (): ReportSettingsValidationErrors => ({
  report_anchor_day: '',
  report_fallback_days: '',
  report_timezone: '',
  columns: '',
  statusLabels: '',
  deviceTypeColumns: {
    electricity: '',
    water: '',
    heating: '',
    urt: '',
  },
});

export const DEFAULT_COLUMNS_PRESET: ReportConfigColumn[] = [
  { key: 'apartment_number', title: '№ квартиры' },
  { key: 'system_type', title: 'Прибор' },
  { key: 'consumption', title: 'Расход' },
  { key: 'status_label', title: 'Статус' },
];

export const DEFAULT_STATUS_LABELS_PRESET: Array<[string, string]> = [
  ['ok', 'Принято'],
  ['missing_both', 'Нет данных'],
  ['missing_current', 'Нет текущих'],
  ['missing_previous', 'Нет прошлых'],
];

export const DEFAULT_ANCHOR_DAY = 25;
export const DEFAULT_FALLBACK_DAYS = 5;
export const DEFAULT_TIMEZONE = 'Europe/Moscow';

export const COMMON_TIMEZONES: string[] = [
  'Europe/Kaliningrad',
  'Europe/Moscow',
  'Europe/Samara',
  'Asia/Yekaterinburg',
  'Asia/Omsk',
  'Asia/Novosibirsk',
  'Asia/Krasnoyarsk',
  'Asia/Irkutsk',
  'Asia/Yakutsk',
  'Asia/Vladivostok',
  'Asia/Magadan',
  'Asia/Kamchatka',
  'UTC',
];

const createRowId = () =>
  `rc-${Math.random().toString(36).slice(2, 7)}-${Date.now().toString(36)}`;

export const createEmptyColumn = (): ReportColumnFormValue => ({
  id: createRowId(),
  key: '',
  title: '',
});

export const createEmptyStatusLabel = (): ReportStatusLabelFormValue => ({
  id: createRowId(),
  key: '',
  label: '',
});

const mapColumnsToForm = (columns?: ReportConfigColumn[] | null): ReportColumnFormValue[] => {
  const source = Array.isArray(columns) && columns.length ? columns : DEFAULT_COLUMNS_PRESET;
  return source.map((column) => ({
    id: createRowId(),
    key: column.key || '',
    title: column.title || '',
  }));
};

const mapTypeColumnsToForm = (
  columns?: ReportConfigColumn[] | null,
): ReportColumnFormValue[] => {
  if (!Array.isArray(columns) || !columns.length) return [];
  return columns.map((column) => ({
    id: createRowId(),
    key: column.key || '',
    title: column.title || '',
  }));
};

const mapStatusLabelsToForm = (
  labels?: ReportConfig['status_labels'],
): ReportStatusLabelFormValue[] => {
  const entries = labels
    ? Object.entries(labels)
    : DEFAULT_STATUS_LABELS_PRESET;
  if (!entries.length) {
    return [createEmptyStatusLabel()];
  }
  return entries.map(([key, label]) => ({
    id: createRowId(),
    key: key || '',
    label: label || '',
  }));
};

export const buildReportSettingsFromComplex = (
  complex?: ResidentialComplexRead | null,
): ReportSettingsFormValue => ({
  report_anchor_day: complex?.report_anchor_day ?? DEFAULT_ANCHOR_DAY,
  report_fallback_days: complex?.report_fallback_days ?? DEFAULT_FALLBACK_DAYS,
  report_timezone: complex?.report_timezone ?? DEFAULT_TIMEZONE,
  export_mode:
    complex?.report_config?.export_mode === 'separate_by_resource'
      ? 'separate_by_resource'
      : 'combined',
  export_format:
    complex?.report_config?.export_format === 'c300_csv' ? 'c300_csv' : 'xlsx',
  columns: mapColumnsToForm(complex?.report_config?.columns),
  statusLabels: mapStatusLabelsToForm(complex?.report_config?.status_labels),
  deviceTypeColumns: {
    electricity: mapTypeColumnsToForm(complex?.report_config?.device_type_columns?.electricity),
    water: mapTypeColumnsToForm(complex?.report_config?.device_type_columns?.water),
    heating: mapTypeColumnsToForm(complex?.report_config?.device_type_columns?.heating),
    urt: mapTypeColumnsToForm(complex?.report_config?.device_type_columns?.urt),
  },
});

export const validateReportSettings = (
  settings: ReportSettingsFormValue,
): ReportSettingsValidationErrors => {
  const errors = createEmptyReportSettingsErrors();

  const anchor = Number(settings.report_anchor_day);
  if (!Number.isFinite(anchor) || anchor < 1 || anchor > 31) {
    errors.report_anchor_day = 'Опорный день должен быть в диапазоне 1-31';
  }

  const fallback = Number(settings.report_fallback_days);
  if (!Number.isFinite(fallback) || fallback < 0 || fallback > 31) {
    errors.report_fallback_days = 'Количество дней должно быть от 0 до 31';
  }

  if (!settings.report_timezone?.trim()) {
    errors.report_timezone = 'Укажите часовой пояс';
  }

  const invalidColumns =
    settings.export_format !== 'c300_csv' &&
    (!settings.columns?.length ||
      settings.columns.some((column) => !column.key?.trim()));
  if (invalidColumns) {
    errors.columns = 'Добавьте колонку и заполните ключ.';
  }

  const invalidStatuses =
    settings.export_format !== 'c300_csv' &&
    (!settings.statusLabels?.length ||
      settings.statusLabels.some(
        (status) => !status.key?.trim() || !status.label?.trim(),
      ));
  if (invalidStatuses) {
    errors.statusLabels = 'Заполните ключ и подпись хотя бы для одного статуса.';
  }



  const deviceTypeErrors: Record<string, string> = { ...errors.deviceTypeColumns };
  Object.entries(settings.deviceTypeColumns || {}).forEach(([typeKey, columns]) => {
    if (settings.export_format === 'c300_csv') {
      deviceTypeErrors[typeKey] = '';
      return;
    }
    if (!columns?.length) {
      deviceTypeErrors[typeKey] = '';
      return;
    }
    const invalid = columns.some((column) => !column.key?.trim());
    deviceTypeErrors[typeKey] = invalid
      ? 'Заполните ключи для выбранных колонок.'
      : '';
  });
  errors.deviceTypeColumns = deviceTypeErrors;

  return errors;
};

export const serializeReportSettings = (settings: ReportSettingsFormValue) => {
  const anchorDay = Number(settings.report_anchor_day);
  const fallbackDays = Number(settings.report_fallback_days);

  const sanitizedColumns = settings.columns
    .map(({ key, title }) => ({
      key: key.trim(),
      title: (title || key).trim(),
    }))
    .filter((item) => item.key.length);

  const statusEntries = settings.statusLabels
    .map(({ key, label }) => [key.trim(), (label || key).trim()] as [string, string])
    .filter(([key]) => key.length);

  const report_config: ReportConfig | undefined =
    sanitizedColumns.length || statusEntries.length || settings.export_mode || settings.export_format
      ? {
          ...(sanitizedColumns.length ? { columns: sanitizedColumns } : {}),
          ...(statusEntries.length ? { status_labels: Object.fromEntries(statusEntries) } : {}),
          export_mode:
            settings.export_mode === 'separate_by_resource'
              ? 'separate_by_resource'
              : 'combined',
          export_format: settings.export_format === 'c300_csv' ? 'c300_csv' : 'xlsx',
        }
      : undefined;

  const deviceTypeColumns = settings.deviceTypeColumns || {};
  const serializedTypeColumns = Object.entries(deviceTypeColumns).reduce(
    (acc, [typeKey, columns]) => {
      const sanitized = (columns || [])
        .map(({ key, title }) => ({
          key: key.trim(),
          title: (title || key).trim(),
        }))
        .filter((item) => item.key.length);
      if (sanitized.length) {
        acc[typeKey] = sanitized;
      }
      return acc;
    },
    {} as Record<string, ReportConfigColumn[]>,
  );

  const report_config_with_types =
    report_config || Object.keys(serializedTypeColumns).length
      ? {
          ...(report_config || {}),
          ...(Object.keys(serializedTypeColumns).length
            ? { device_type_columns: serializedTypeColumns }
            : {}),
        }
      : undefined;

  return {
    report_anchor_day: Number.isFinite(anchorDay) ? anchorDay : undefined,
    report_fallback_days: Number.isFinite(fallbackDays) ? fallbackDays : undefined,
    report_timezone: settings.report_timezone?.trim() || undefined,
    report_config: report_config_with_types,
  };
};

export const createDefaultReportSettings = (): ReportSettingsFormValue =>
  buildReportSettingsFromComplex();
